import { BaseReferenceDto, ContentStep } from '@cfa/pathway-fe-shared/xp';

import { LanguageObject } from '@/types';

import { getStepResource } from './contentStep';
import { getNameFromLanguage, itemByLanguage } from './language';

import type { ChecklistSectionStepDto } from '@/_generated/api-clients/content/types.gen';

/**
 * @deprecated Reads reference name from ChecklistSectionStepDto; migrate callers to use ContentStep.resource.references directly. Will be removed in PATHWAY-3174.
 * This will not have a replacement since reference is not on ContentStep root.
 */
export const getStepReferenceName = (
  step: ChecklistSectionStepDto,
  language: string,
) => {
  const stepReference = getNameFromLanguage(
    step.reference?.references as LanguageObject,
    language,
    true,
  ) as BaseReferenceDto;
  const stepReferenceName =
    stepReference.name ??
    getNameFromLanguage(step.reference?.name as LanguageObject, language, true);
  return stepReferenceName;
};

/**
 * @deprecated Handles both ContentStep and ChecklistSectionStepDto; migrate callers to use ContentStep.resource.references directly. Will be removed in PATHWAY-3174.
 * Use getPlanStepResourceReferenceName for ContentStep and getStepReferenceName for ChecklistSectionStepDto instead. This function exists to handle the legacy ChecklistSectionStepDto shape that contains a reference object, as well as the newer ContentStep shape where references are nested under resource. Will be removed once all consumers have migrated to the ContentStep shape.
 */
export const getStepResourceReferenceName = (
  step: ContentStep | ChecklistSectionStepDto,
  language: string,
) => {
  if ('resource' in step && step.resource?.references) {
    const matchingReference = step.resource.references.find(
      ref => ref.language.toLowerCase() === language.toLowerCase(),
    );
    return matchingReference?.name ?? step.resource.references[0]?.name;
  }
  return getStepReferenceName(step as ChecklistSectionStepDto, language);
};

export const getPlanStepResourceReferenceName = (
  step: ContentStep,
  language: string,
) => {
  const references = getStepResource(step)?.references;
  return (
    references?.find(
      ref => ref.language.toLowerCase() === language.toLowerCase(),
    )?.name ??
    references?.[0]?.name ??
    null
  );
};

/**
 * @deprecated Reads step name from ChecklistSectionStepDto; migrate callers to derive name from ContentStep directly. Will be removed in PATHWAY-3174.
 * Use getPlanStepResourceReferenceName for ContentStep and getStepReferenceName for ChecklistSectionStepDto instead. This function exists to handle the legacy ChecklistSectionStepDto shape that contains a reference object, as well as the newer ContentStep shape where references are nested under resource. Will be removed once all consumers have migrated to the ContentStep shape.
 */
export const getStepNameByLang = (
  step: ChecklistSectionStepDto,
  language: string,
  strict?: boolean,
) => {
  const stepReferenceName = getStepReferenceName(step, language);
  const stepName = getNameFromLanguage(
    step.name as LanguageObject,
    language,
    strict,
  );
  return !stepReferenceName ? stepName : stepReferenceName;
};

export const getPlanStepNameByLang = (step: ContentStep, language: string) => {
  const stepResourceReferenceName = getPlanStepResourceReferenceName(
    step,
    language,
  );
  if (stepResourceReferenceName) {
    return stepResourceReferenceName;
  }
  return itemByLanguage(step.name, language);
};
